import { useEffect, useState } from 'react';
import { Link, Navigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Loader2 } from 'lucide-react';
import logger from '@/lib/logging';
import { verifyCheckoutSessionApi } from '@/lib/billing-api';
import { clearPendingPurchasePriceId } from '@/lib/purchase-handoff-client';
import { BROWSE_PLANS_PATH } from '@/components/subscriptions/lib/plans-path';

export function PurchaseSuccessPage() {
  const { isAuthenticated, loading: authLoading, refreshServerSession } = useAuth();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id')?.trim() ?? '';
  const [verifying, setVerifying] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (authLoading || !isAuthenticated || !sessionId) return;

    let cancelled = false;
    async function verify() {
      setVerifying(true);
      try {
        const data = await verifyCheckoutSessionApi(sessionId);
        const verified = Boolean(data.ready ?? data.success);
        if (verified) {
          // Purchase done — later logins in this tab must not carry the handoff.
          clearPendingPurchasePriceId();
          // The request-decision cache can still hold the pre-payment verdict
          // for up to its TTL — refresh the session before navigating so
          // /dashboard doesn't bounce the new buyer to /signup-required.
          await refreshServerSession();
        }
        if (!cancelled) {
          setReady(verified);
        }
      } catch (err) {
        logger.error('[PurchaseSuccessPage] verify failed', err);
        if (!cancelled) {
          setError(
            err instanceof Error
              ? err.message
              : 'Payment verification failed. Please contact support if you were charged.'
          );
        }
      } finally {
        if (!cancelled) setVerifying(false);
      }
    }

    void verify();
    return () => {
      cancelled = true;
    };
  }, [authLoading, isAuthenticated, sessionId, refreshServerSession]);

  if (authLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!isAuthenticated) {
    const returnTo = `/purchase/success?session_id=${encodeURIComponent(sessionId)}`;
    return <Navigate to={`/login?returnTo=${encodeURIComponent(returnTo)}`} replace />;
  }

  if (!sessionId) {
    return <Navigate to={BROWSE_PLANS_PATH} replace />;
  }

  if (ready) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-4">
      <Card className="w-full max-w-lg">
        <CardHeader>
          <CardTitle>{error ? 'Verification issue' : 'Confirming payment'}</CardTitle>
          <CardDescription>
            {error
              ? 'We could not confirm your payment automatically.'
              : 'Hang tight while we activate your subscription.'}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {verifying && !error ? (
            <div className="flex items-center gap-3 text-muted-foreground">
              <Loader2 className="h-5 w-5 animate-spin" />
              <span>Processing payment…</span>
            </div>
          ) : null}

          {error ? (
            <Alert variant="destructive">
              <AlertTitle>Verification failed</AlertTitle>
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          ) : null}

          <div className="flex flex-col gap-3 sm:flex-row">
            <Button asChild variant={error ? 'default' : 'outline'}>
              <Link to="/dashboard">Go to dashboard</Link>
            </Button>
            <Button asChild variant="outline">
              <Link to={BROWSE_PLANS_PATH}>Back to plans</Link>
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

export default PurchaseSuccessPage;
